import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { 
  Map, 
  ArrowLeft,
  Loader2,
  Download,
  Eye,
  Clock,
  CheckCircle2,
  PlayCircle,
  Search,
  FileImage,
  Building2,
  Users,
  Layers,
  StickyNote,
  Calendar,
  ExternalLink,
  FileText,
  Files
} from "lucide-react";
import { PiktableLoader } from "@/components/PiktableLoader";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

interface TableSnapshot {
  id: string;
  name: string;
  capacity: number;
  is_bookable: boolean;
  zone_name?: string;
}

interface ZoneSnapshot {
  id: string;
  name: string;
  color?: string;
}

interface FloorPlanRequest {
  id: string;
  establishment_id: string;
  user_id: string;
  user_email: string;
  file_path: string;
  file_name: string;
  file_paths?: string[];
  file_names?: string[];
  tables_snapshot: TableSnapshot[];
  zones_snapshot: ZoneSnapshot[] | null;
  positioning_notes: string | null;
  combination_notes: string | null;
  status: string;
  admin_notes: string | null;
  completed_at: string | null;
  completed_by: string | null;
  created_at: string;
  updated_at: string;
  establishment?: {
    id: string;
    name: string;
    slug: string;
  };
}

type StatusFilter = "all" | "pending" | "in_progress" | "completed";

export default function AdminFloorPlanRequests() {
  const [requests, setRequests] = useState<FloorPlanRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRequest, setSelectedRequest] = useState<FloorPlanRequest | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [adminNotes, setAdminNotes] = useState("");
  const [updating, setUpdating] = useState(false);
  const [fileUrls, setFileUrls] = useState<{ url: string; name: string }[]>([]);
  const { toast } = useToast();

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('floor_plan_setup_requests')
        .select(`
          *,
          establishment:establishments(id, name, slug)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Parse JSON fields
      const parsedData = (data || []).map(request => ({
        ...request,
        tables_snapshot: typeof request.tables_snapshot === 'string' 
          ? JSON.parse(request.tables_snapshot) 
          : request.tables_snapshot || [],
        zones_snapshot: request.zones_snapshot 
          ? (typeof request.zones_snapshot === 'string' 
              ? JSON.parse(request.zones_snapshot) 
              : request.zones_snapshot)
          : null,
        file_paths: (Array.isArray(request.file_paths) 
          ? request.file_paths 
          : (request.file_path ? [request.file_path] : [])) as string[],
        file_names: (Array.isArray(request.file_names) 
          ? request.file_names 
          : (request.file_name ? [request.file_name] : [])) as string[]
      }));

      setRequests(parsedData as FloorPlanRequest[]);
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les demandes",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getSignedUrl = async (filePath: string) => {
    try {
      const { data, error } = await supabase.storage
        .from('floor-plan-requests')
        .createSignedUrl(filePath, 3600); // 1 hour

      if (error) throw error;
      return data.signedUrl;
    } catch (error) {
      console.error('Error getting signed URL:', error);
      return null;
    }
  };

  const getSignedUrlsForRequest = async (request: FloorPlanRequest) => {
    const paths = request.file_paths || (request.file_path ? [request.file_path] : []);
    const names = request.file_names || (request.file_name ? [request.file_name] : []);
    
    const urls: { url: string; name: string }[] = [];
    
    for (let i = 0; i < paths.length; i++) {
      const url = await getSignedUrl(paths[i]);
      if (url) {
        urls.push({ url, name: names[i] || `Fichier ${i + 1}` });
      }
    }
    
    return urls;
  };

  const openDetails = async (request: FloorPlanRequest) => {
    setSelectedRequest(request);
    setAdminNotes(request.admin_notes || "");
    
    // Get signed URLs for all files
    const urls = await getSignedUrlsForRequest(request);
    setFileUrls(urls);
    
    setDetailsOpen(true);
  };

  const updateStatus = async (request: FloorPlanRequest, newStatus: string) => {
    try {
      setUpdating(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      
      const updates: any = {
        status: newStatus,
        admin_notes: adminNotes,
        updated_at: new Date().toISOString()
      };

      if (newStatus === 'completed') {
        updates.completed_at = new Date().toISOString();
        updates.completed_by = user?.id;
      }

      const { error } = await supabase
        .from('floor_plan_setup_requests')
        .update(updates)
        .eq('id', request.id);

      if (error) throw error;

      // Send confirmation email when marking as completed
      if (newStatus === 'completed') {
        try {
          const { error: emailError } = await supabase.functions.invoke('send-floor-plan-completed', {
            body: {
              requestId: request.id,
              establishmentName: request.establishment?.name || 'Votre établissement',
              establishmentSlug: request.establishment?.slug || '',
              userEmail: request.user_email,
              tablesCount: Array.isArray(request.tables_snapshot) ? request.tables_snapshot.length : 0,
              zonesCount: request.zones_snapshot?.length || 0,
              adminNotes: adminNotes || null
            }
          });

          if (emailError) {
            console.error('Error sending completion email:', emailError);
            toast({
              title: "Statut mis à jour",
              description: "Le plan est terminé mais l'email de confirmation n'a pas pu être envoyé",
            });
          } else {
            toast({
              title: "Plan terminé ✓",
              description: "Un email de confirmation a été envoyé au client"
            });
          }
        } catch (emailErr) {
          console.error('Error invoking email function:', emailErr);
          toast({
            title: "Statut mis à jour",
            description: "Le plan est terminé mais l'email n'a pas pu être envoyé",
          });
        }
      } else {
        toast({
          title: "Statut mis à jour",
          description: `La demande est maintenant "${getStatusLabel(newStatus)}"`
        });
      }

      await fetchRequests();
      setDetailsOpen(false);
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour le statut",
        variant: "destructive"
      });
    } finally {
      setUpdating(false);
    }
  };

  const saveAdminNotes = async () => {
    if (!selectedRequest) return;

    try {
      setUpdating(true);
      const { error } = await supabase
        .from('floor_plan_setup_requests')
        .update({ 
          admin_notes: adminNotes,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedRequest.id);

      if (error) throw error;

      toast({
        title: "Notes sauvegardées"
      });

      await fetchRequests();
    } catch (error) {
      console.error('Error saving notes:', error);
      toast({
        title: "Erreur",
        description: "Impossible de sauvegarder les notes",
        variant: "destructive"
      });
    } finally {
      setUpdating(false);
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending': return 'En attente';
      case 'in_progress': return 'En cours';
      case 'completed': return 'Terminé';
      default: return status;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20">
            <Clock className="w-3 h-3 mr-1" />
            En attente
          </Badge>
        );
      case 'in_progress':
        return (
          <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/20">
            <PlayCircle className="w-3 h-3 mr-1" />
            En cours
          </Badge>
        );
      case 'completed':
        return (
          <Badge className="bg-green-500/10 text-green-600 border-green-500/20">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Terminé
          </Badge>
        );
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getFilesCount = (request: FloorPlanRequest) => {
    const paths = request.file_paths || (request.file_path ? [request.file_path] : []);
    return paths.length;
  };

  // Filter requests
  const filteredRequests = requests.filter(request => {
    const matchesStatus = statusFilter === 'all' || request.status === statusFilter;
    const matchesSearch = searchQuery === '' || 
      request.establishment?.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      request.user_email.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  // Stats
  const stats = {
    total: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    inProgress: requests.filter(r => r.status === 'in_progress').length,
    completed: requests.filter(r => r.status === 'completed').length
  };

  if (loading) {
    return <PiktableLoader message="Chargement des demandes..." />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background">
      <div className="container mx-auto p-6 max-w-7xl space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <Link to="/admin" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-2">
              <ArrowLeft className="w-4 h-4" />
              Retour à l'administration
            </Link>
            <h1 className="text-3xl font-serif font-bold flex items-center gap-3">
              <Map className="w-8 h-8 text-primary" />
              Demandes plan de salle
              {stats.pending > 0 && (
                <Badge variant="destructive" className="ml-2">
                  {stats.pending} en attente
                </Badge>
              )}
            </h1>
            <p className="text-muted-foreground mt-1">
              Gérez les demandes d'assistance de configuration
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-slate-500/10 to-slate-600/5 border-slate-500/20">
            <CardHeader className="pb-2">
              <CardDescription>Total</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.total}</div>
              <p className="text-sm text-muted-foreground">demandes</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/20">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                En attente
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-amber-600">{stats.pending}</div>
              <p className="text-sm text-muted-foreground">à traiter</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <PlayCircle className="w-4 h-4" />
                En cours
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">{stats.inProgress}</div>
              <p className="text-sm text-muted-foreground">en traitement</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                Terminées
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">{stats.completed}</div>
              <p className="text-sm text-muted-foreground">complétées</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher par établissement ou email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
                <SelectTrigger className="w-full sm:w-48">
                  <SelectValue placeholder="Filtrer par statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="in_progress">En cours</SelectItem>
                  <SelectItem value="completed">Terminé</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Requests Table */}
        <Card>
          <CardContent className="pt-6">
            {filteredRequests.length === 0 ? (
              <div className="text-center py-12">
                <Map className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  {searchQuery || statusFilter !== 'all' 
                    ? "Aucune demande ne correspond aux filtres"
                    : "Aucune demande d'assistance"}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Établissement</TableHead>
                      <TableHead>Demandeur</TableHead>
                      <TableHead>Fichiers</TableHead>
                      <TableHead className="text-center">Tables</TableHead>
                      <TableHead className="text-center">Zones</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRequests.map((request) => {
                      const filesCount = getFilesCount(request);
                      return (
                        <TableRow key={request.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-muted-foreground" />
                              <div>
                                <p className="font-medium">{request.establishment?.name || 'N/A'}</p>
                                <p className="text-xs text-muted-foreground">/{request.establishment?.slug}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">{request.user_email}</span>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="gap-1">
                              {filesCount > 1 ? (
                                <>
                                  <Files className="w-3 h-3" />
                                  {filesCount} fichiers
                                </>
                              ) : (
                                <>
                                  <FileImage className="w-3 h-3" />
                                  1 fichier
                                </>
                              )}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline">
                              {Array.isArray(request.tables_snapshot) ? request.tables_snapshot.length : 0}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline">
                              {request.zones_snapshot ? request.zones_snapshot.length : 0}
                            </Badge>
                          </TableCell>
                          <TableCell>{getStatusBadge(request.status)}</TableCell>
                          <TableCell>
                            <span className="text-sm text-muted-foreground">
                              {format(new Date(request.created_at), 'dd MMM yyyy', { locale: fr })}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openDetails(request)}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Détails
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Details Dialog */}
        <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
          <DialogContent className="max-w-4xl h-[90vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Map className="w-5 h-5" />
                Détails de la demande
              </DialogTitle>
              <DialogDescription>
                {selectedRequest?.establishment?.name} - {selectedRequest?.user_email}
              </DialogDescription>
            </DialogHeader>

            <ScrollArea className="flex-1 pr-4">
              {selectedRequest && (
                <div className="space-y-6">
                  {/* Status and Info */}
                  <div className="flex items-center justify-between">
                    {getStatusBadge(selectedRequest.status)}
                    <span className="text-sm text-muted-foreground">
                      <Calendar className="w-4 h-4 inline mr-1" />
                      {format(new Date(selectedRequest.created_at), 'dd MMMM yyyy à HH:mm', { locale: fr })}
                    </span>
                  </div>

                  {/* Files Preview - Multiple Files */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      {fileUrls.length > 1 ? (
                        <Files className="w-4 h-4" />
                      ) : (
                        <FileImage className="w-4 h-4" />
                      )}
                      Fichiers uploadés ({fileUrls.length})
                    </Label>
                    <div className="border rounded-lg p-4">
                      {fileUrls.length > 0 ? (
                        <div className="space-y-4">
                          {/* Image gallery for images */}
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {fileUrls.map((file, index) => {
                              const isImage = file.name.match(/\.(jpg|jpeg|png|gif|webp)$/i);
                              return (
                                <div key={index} className="relative group">
                                  {isImage ? (
                                    <a href={file.url} target="_blank" rel="noopener noreferrer">
                                      <img 
                                        src={file.url} 
                                        alt={file.name} 
                                        className="h-32 w-full object-cover rounded-lg border hover:opacity-90 transition-opacity"
                                      />
                                    </a>
                                  ) : (
                                    <a 
                                      href={file.url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="h-32 w-full flex flex-col items-center justify-center rounded-lg border bg-muted hover:bg-muted/80 transition-colors"
                                    >
                                      <FileText className="w-8 h-8 text-muted-foreground mb-2" />
                                      <span className="text-xs text-muted-foreground text-center px-2 truncate max-w-full">
                                        {file.name}
                                      </span>
                                    </a>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          
                          {/* Files list with download buttons */}
                          <div className="space-y-2 pt-3 border-t">
                            {fileUrls.map((file, index) => (
                              <div 
                                key={index}
                                className="flex items-center justify-between p-2 rounded-lg bg-muted/50"
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  {file.name.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                                    <FileImage className="w-4 h-4 text-muted-foreground shrink-0" />
                                  ) : (
                                    <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                                  )}
                                  <span className="text-sm truncate">{file.name}</span>
                                </div>
                                <div className="flex gap-1 shrink-0">
                                  <Button variant="ghost" size="sm" asChild>
                                    <a href={file.url} target="_blank" rel="noopener noreferrer">
                                      <ExternalLink className="w-4 h-4" />
                                    </a>
                                  </Button>
                                  <Button variant="ghost" size="sm" asChild>
                                    <a href={file.url} download={file.name}>
                                      <Download className="w-4 h-4" />
                                    </a>
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <p className="text-center text-muted-foreground py-4">
                          Impossible de charger les fichiers
                        </p>
                      )}
                    </div>
                  </div>

                  <Separator />

                  {/* Tables */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Tables ({Array.isArray(selectedRequest.tables_snapshot) ? selectedRequest.tables_snapshot.length : 0})
                    </Label>
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Nom</TableHead>
                            <TableHead>Capacité</TableHead>
                            <TableHead>Zone</TableHead>
                            <TableHead>Réservable</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {Array.isArray(selectedRequest.tables_snapshot) && selectedRequest.tables_snapshot.map((table, index) => (
                            <TableRow key={table.id || index}>
                              <TableCell className="font-medium">{table.name}</TableCell>
                              <TableCell>{table.capacity} pers.</TableCell>
                              <TableCell>{table.zone_name || '-'}</TableCell>
                              <TableCell>
                                {table.is_bookable ? (
                                  <Badge className="bg-green-500/10 text-green-600">Oui</Badge>
                                ) : (
                                  <Badge variant="secondary">Non</Badge>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>

                  {/* Zones */}
                  {selectedRequest.zones_snapshot && selectedRequest.zones_snapshot.length > 0 && (
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Layers className="w-4 h-4" />
                        Zones ({selectedRequest.zones_snapshot.length})
                      </Label>
                      <div className="flex flex-wrap gap-2">
                        {selectedRequest.zones_snapshot.map((zone, index) => (
                          <Badge 
                            key={zone.id || index} 
                            variant="outline"
                            style={{ borderColor: zone.color, color: zone.color }}
                          >
                            {zone.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  <Separator />

                  {/* User Notes */}
                  <div className="space-y-4">
                    {selectedRequest.combination_notes && (
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <StickyNote className="w-4 h-4" />
                          Notes combinaison de tables
                        </Label>
                        <div className="bg-muted/50 rounded-lg p-3 text-sm">
                          {selectedRequest.combination_notes}
                        </div>
                      </div>
                    )}

                    {selectedRequest.positioning_notes && (
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <StickyNote className="w-4 h-4" />
                          Notes positionnement
                        </Label>
                        <div className="bg-muted/50 rounded-lg p-3 text-sm">
                          {selectedRequest.positioning_notes}
                        </div>
                      </div>
                    )}
                  </div>

                  <Separator />

                  {/* Admin Notes */}
                  <div className="space-y-2">
                    <Label htmlFor="admin-notes">Notes administrateur</Label>
                    <Textarea
                      id="admin-notes"
                      placeholder="Ajouter des notes internes ou un message personnalisé pour le client..."
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      rows={3}
                    />
                    <p className="text-xs text-muted-foreground">
                      💡 Ces notes seront incluses dans l'email de confirmation envoyé au client lors de la finalisation
                    </p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={saveAdminNotes}
                      disabled={updating}
                    >
                      {updating && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                      Sauvegarder les notes
                    </Button>
                  </div>
                </div>
              )}
            </ScrollArea>

            <DialogFooter className="flex-shrink-0 pt-4 border-t">
              {selectedRequest && (
                <div className="flex gap-2 w-full justify-between">
                  <Button variant="outline" onClick={() => setDetailsOpen(false)}>
                    Fermer
                  </Button>
                  <div className="flex gap-2">
                    {selectedRequest.status === 'pending' && (
                      <Button 
                        onClick={() => updateStatus(selectedRequest, 'in_progress')}
                        disabled={updating}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        {updating && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                        <PlayCircle className="w-4 h-4 mr-1" />
                        Marquer en cours
                      </Button>
                    )}
                    {selectedRequest.status === 'in_progress' && (
                      <Button 
                        onClick={() => updateStatus(selectedRequest, 'completed')}
                        disabled={updating}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {updating && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        Terminer et notifier le client
                      </Button>
                    )}
                    {selectedRequest.status === 'completed' && (
                      <Button 
                        variant="outline"
                        onClick={() => updateStatus(selectedRequest, 'in_progress')}
                        disabled={updating}
                      >
                        {updating && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                        Rouvrir la demande
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
